import { Node, mergeAttributes } from '@tiptap/core';
import { TextStyle } from '@tiptap/extension-text-style';
import type { RichTextPresetStyle } from '@/services/rich-text-presets.api';

export const StyledText = TextStyle.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            fontSize: {
                default: null,
                parseHTML: (element) => element.style.fontSize || null,
                renderHTML: (attributes) => {
                    if (!attributes.fontSize) return {};
                    return { style: `font-size: ${attributes.fontSize}` };
                },
            },
            fontFamily: {
                default: null,
                parseHTML: (element) => element.style.fontFamily?.replace(/['"]/g, '') || null,
                renderHTML: (attributes) => {
                    if (!attributes.fontFamily) return {};
                    return { style: `font-family: ${attributes.fontFamily}` };
                },
            },
        };
    },
});

export const CALLOUT_DEFAULTS = {
    iconType: 'vector',
    icon: 'info',
    iconEmoji: '💬',
    backgroundColor: '#BDE5F8',
    borderColor: '#2B6CB0',
    textColor: '#1B5F9E',
    iconColor: '#FFFFFF',
    padding: '16px',
    fontFamily: 'Helvetica',
    fontSize: '14px',
    fontWeight: '400',
    fontStyle: 'normal',
    textDecoration: 'none',
};

export const getCalloutIconLabel = (style: RichTextPresetStyle) => {
    if (style.boxIconType === 'emoji') return style.boxIconEmoji || '💬';
    if (!style.boxIcon || style.boxIcon === 'none') return '';
    if (style.boxIcon === 'info') return 'i';
    if (style.boxIcon === 'warning') return '!';
    if (style.boxIcon === 'check') return '✓';
    if (style.boxIcon === 'bell') return '●';
    if (style.boxIcon === 'star') return '*';
    return '!';
};

export const getCalloutIconSpec = (attrs: Record<string, string>) => {
    if (attrs.iconType === 'emoji') return attrs.iconEmoji || '💬';
    if (!attrs.icon || attrs.icon === 'none') return '';

    const svgAttrs = {
        xmlns: 'http://www.w3.org/2000/svg',
        width: '16',
        height: '16',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2.4',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        style: 'display: block;',
    };

    if (attrs.icon === 'warning') {
        return ['svg', svgAttrs, ['path', { d: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z' }], ['path', { d: 'M12 9v4' }], ['path', { d: 'M12 17h.01' }]];
    }
    if (attrs.icon === 'check') {
        return ['svg', svgAttrs, ['path', { d: 'm5 12 5 5L20 7' }]];
    }
    if (attrs.icon === 'bell') {
        return ['svg', svgAttrs, ['path', { d: 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9' }], ['path', { d: 'M13.7 21a2 2 0 0 1-3.4 0' }]];
    }
    if (attrs.icon === 'star') {
        return ['svg', svgAttrs, ['path', { d: 'm12 2 3.1 6.4 6.9 1-5 4.9 1.2 6.9L12 18l-6.2 3.2L7 14.3 2 9.4l6.9-1L12 2Z' }]];
    }
    if (attrs.icon === 'exclamation') {
        return ['svg', svgAttrs, ['path', { d: 'M12 8v5' }], ['path', { d: 'M12 17h.01' }], ['path', { d: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z' }]];
    }
    return ['svg', svgAttrs, ['path', { d: 'M12 16v-4' }], ['path', { d: 'M12 8h.01' }], ['path', { d: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z' }]];
};

export const CalloutBox = Node.create({
    name: 'calloutBox',
    group: 'block',
    content: 'inline*',
    defining: true,
    selectable: true,

    addAttributes() {
        const dataAttribute = (name: string) => `data-callout-${name}`;
        const attribute = (attrName: string, dataName: string, defaultValue: string) => ({
            default: defaultValue,
            parseHTML: (element: HTMLElement) => element.getAttribute(dataAttribute(dataName)) || defaultValue,
            renderHTML: (attributes: Record<string, string>) => ({
                [dataAttribute(dataName)]: attributes[attrName],
            }),
        });

        return {
            iconType: attribute('iconType', 'icon-type', CALLOUT_DEFAULTS.iconType),
            icon: attribute('icon', 'icon', CALLOUT_DEFAULTS.icon),
            iconEmoji: attribute('iconEmoji', 'icon-emoji', CALLOUT_DEFAULTS.iconEmoji),
            backgroundColor: attribute('backgroundColor', 'background-color', CALLOUT_DEFAULTS.backgroundColor),
            borderColor: attribute('borderColor', 'border-color', CALLOUT_DEFAULTS.borderColor),
            textColor: attribute('textColor', 'text-color', CALLOUT_DEFAULTS.textColor),
            iconColor: attribute('iconColor', 'icon-color', CALLOUT_DEFAULTS.iconColor),
            padding: attribute('padding', 'padding', CALLOUT_DEFAULTS.padding),
            fontFamily: attribute('fontFamily', 'font-family', CALLOUT_DEFAULTS.fontFamily),
            fontSize: attribute('fontSize', 'font-size', CALLOUT_DEFAULTS.fontSize),
            fontWeight: attribute('fontWeight', 'font-weight', CALLOUT_DEFAULTS.fontWeight),
            fontStyle: attribute('fontStyle', 'font-style', CALLOUT_DEFAULTS.fontStyle),
            textDecoration: attribute('textDecoration', 'text-decoration', CALLOUT_DEFAULTS.textDecoration),
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-rich-text-preset-box]' }];
    },

    renderHTML({ node, HTMLAttributes }: any) {
        const attrs = { ...CALLOUT_DEFAULTS, ...node.attrs };
        const icon = getCalloutIconSpec(attrs);
        const boxStyle = [
            `border: 1px solid ${attrs.borderColor}`,
            `background-color: ${attrs.backgroundColor}`,
            `padding: ${attrs.padding}`,
            'border-radius: 0',
            'display: flex',
            'align-items: flex-start',
            'gap: 14px',
            'margin: 12px 0',
            `color: ${attrs.textColor}`,
            'box-sizing: border-box',
        ].join('; ');
        const textStyle = [
            `font-family: ${attrs.fontFamily}`,
            `font-size: ${attrs.fontSize}`,
            `color: ${attrs.textColor}`,
            `font-weight: ${attrs.fontWeight}`,
            `font-style: ${attrs.fontStyle}`,
            `text-decoration: ${attrs.textDecoration}`,
            'line-height: 1.35',
        ].join('; ');
        const iconNode = icon
            ? [
                'span',
                {
                    'data-rich-text-preset-icon': 'true',
                    style: [
                        'width: 28px',
                        'height: 28px',
                        'border-radius: 50%',
                        `background-color: ${attrs.iconColor}`,
                        `color: ${attrs.textColor}`,
                        'display: inline-flex',
                        'align-items: center',
                        'justify-content: center',
                        'font-weight: 700',
                        'font-family: Helvetica, Arial, sans-serif',
                        'flex: 0 0 auto',
                    ].join('; '),
                },
                ...(Array.isArray(icon) ? [icon] : [icon]),
            ]
            : null;

        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-rich-text-preset-box': 'true',
                style: boxStyle,
            }),
            ...(iconNode ? [iconNode] : []),
            ['span', { style: textStyle }, 0],
        ];
    },
});
